/**
 * Pencil sketch ideas — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_doodles`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const pencilSketchIdeasPack: ToilePack = {
    slug: 'pencil-sketch-ideas',
    category: 'coloring',
    name: 'Pencil sketch ideas',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'stilllife', label: 'Still life', subject: 'a still life of a teapot, two cups and a folded cloth on a table, the shadows falling to one side'},
        {key: 'street', label: 'Street corner', subject: 'a narrow street with a lamppost, a bicycle leaning on a wall and a café awning, drawn in one-point perspective'},
        {key: 'tree', label: 'Old tree', subject: 'an old tree with a thick knotted trunk, bare branches and its roots gripping a small rock'},
    ],
    styles: [
        {key: 'loose', label: 'Loose HB', style: 'a loose HB pencil sketch with visible construction strokes and soft shading in the darker areas', palette: 'Graphite gray on white paper.', framing: 'The sketch sits on the page with generous white paper around it.'},
        {key: 'hatch', label: 'Cross-hatch', style: 'crisp hatching and cross-hatching in sharpened pencil, tone built from layers of fine lines', palette: 'Graphite gray on white paper.', framing: 'The sketch sits on the page with generous white paper around it.'},
        {key: 'blend', label: 'Blended', style: 'soft blended graphite with smudged shadows and a few firm contour lines', palette: 'Graphite gray from light to dark on white paper.', framing: 'The sketch sits on the page with generous white paper around it.'},
    ],
    directions: ['a boot', 'a window', 'a bicycle'],
    directionHint: 'try: a boot',
    seo: {
        title: 'Pencil Sketch Ideas: Still Life, Street, Old Tree',
        metaDescription: 'Nine pencil sketch ideas, a still life, a street corner and an old tree, in loose HB, cross-hatching and blended graphite.',
        h1: 'Pencil sketch ideas: still life, street corner, old tree',
        tagline: 'Still objects and quiet places, sketched in pencil with visible strokes.',
        intro: 'Pencil sketch ideas are subjects to draw in pencil when you want a drawing worth keeping. These nine pages show pencil sketches of still objects and quiet places, with visible strokes and soft graphite shading: a still life, a street corner and an old tree. Each comes as a loose HB sketch, in cross-hatching, or in blended graphite. For another subject, type it in the direction box.',
        useCases: [
            'A subject for a sketchbook page',
            'A practice drawing in pencil',
            'A reference for shading',
            'A quiet drawing session',
        ],
        faq: [
            {q: 'What is the difference between the three styles?', a: 'Loose HB keeps the construction strokes visible, cross-hatching builds tone from layers of fine lines, and blended graphite uses soft smudged shadows with firm contour lines.'},
            {q: 'Are these photographs?', a: 'No. They are pencil drawings made to look like sketches, not photographs of objects.'},
            {q: 'Can I ask for a different subject?', a: 'Yes. Type it in the direction box, such as a boot, a window or a bicycle. A typed subject is a request, so the result may differ from the samples.'},
        ],
    },
}
